import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import FunnelQuestionScreen from "@common/components/funnel/FunnelQuestionScreen"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import { SCENE_BACKGROUND } from "@common/components/funnel/FunnelScene"
import bubbleIllustration from "@assets/stage/funnel/bubble.webp"
import { apiRequest } from "@data/redux/actions/api"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { useInscriptionFunnelGuard } from "./hooks/useInscriptionFunnelGuard"
import { useInscriptionLead } from "./hooks/useInscriptionLead"
import { inscriptionFunnelService } from "./services/inscriptionFunnelService"
import {
    INSCRIPTION_BESOINS,
    INSCRIPTION_BESOINS_HELPER,
    INSCRIPTION_QUESTIONS,
    INSCRIPTION_STEPS,
    toggleBesoinInList,
} from "./constants/inscriptionContent"

/**
 * Étape diagnostic — une seule question, « une question par écran » (même
 * template que l'été et le stage) : les spécificités de l'élève (multi,
 * optionnel — chips dys/TDAH/HPI…, « Aucune » exclusive).
 *
 * Le niveau ressenti global n'est plus demandé : le curseur d'objectif par
 * matière du compositeur dit où le parent veut aller, matière par matière — ce
 * qu'un « moyen » global ne disait pas. Les libellés sont déseasonnalisés : ce
 * funnel se vend en janvier comme en septembre.
 */
const InscriptionDiagnosticScreen = (props) => {
    const { navigation } = props
    const { state, update } = useInscriptionFunnel()
    const { userParam, nextParams } = useInscriptionFunnelGuard()
    const { funnelStateId } = useInscriptionLead()

    const [besoinsSpeciaux, setBesoinsSpeciaux] = useState(
        state.besoinsSpeciaux || []
    )

    const firstname = state.studentFirstname || "votre enfant"

    // Pas de classe → retour à l'étape 1 : elle pilote le catalogue de
    // matières servi plus loin. Quand un `user` est présent sur un contexte
    // froid, la garde gère déjà la reprise : on ne double pas la redirection.
    useEffect(() => {
        if (!state.studentClass && !userParam) {
            navigation.navigate("inscription/start")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const submitDiagnostic = () => {
        update({ besoinsSpeciaux })

        const stateId = funnelStateId
        if (stateId) {
            props.apiRequest(
                inscriptionFunnelService.updateInscriptionAnswers,
                { stateId },
                {
                    // Même vide, la réponse part : c'est elle qui boucle le
                    // diagnostic côté serveur.
                    besoinsSpeciaux,
                    // Re-send the class so it is persisted even if the class
                    // step's own save was skipped (funnelStateId not yet synced).
                    studentClass: state.studentClass,
                }
            )
        }

        amplitude.track("inscription_funnel_diagnostic_completed", {
            funnel_state_id: stateId,
            besoins_speciaux: besoinsSpeciaux,
        })

        navigation.navigate("inscription/configuration", nextParams)
    }

    // Optionnel → le CTA reste actif même sans sélection.
    return (
        <FunnelQuestionScreen
            illustration={bubbleIllustration}
            illustrationBgColor={SCENE_BACKGROUND}
            question={INSCRIPTION_QUESTIONS.besoins(firstname)}
            helper={INSCRIPTION_BESOINS_HELPER}
            options={INSCRIPTION_BESOINS.map((b) => ({
                id: b.id,
                label: b.label,
            }))}
            selectedIds={besoinsSpeciaux}
            onToggle={(id) =>
                setBesoinsSpeciaux((prev) => toggleBesoinInList(prev, id))
            }
            chipGrid
            multi
            canContinue
            ctaLabel="Continuer"
            onContinue={submitDiagnostic}
            step={INSCRIPTION_STEPS.besoins}
            total={INSCRIPTION_STEPS.total}
            onBack={() => navigation.goBack()}
            HeaderComponent={FunnelStepHeader}
        />
    )
}

export default connect(null, { apiRequest })(InscriptionDiagnosticScreen)
